import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Button, Cards, DraggableTable, Text } from '@components';
import { getDashboardTrafficLogsColumns } from '@constants';
import { DashboardEndpoint, FormType, Translation, VariantType } from '@enums';
import { useAppDispatch, useFormLastUpdated, useFormList, useFormLoadingStatus } from '@hooks';
import { cancelAutoReload, fetchDashboardGraph } from '@store';
import { cn, formatTimestamp } from '@utils';

export const LogPage = () => {
  const dispatch = useAppDispatch();
  const formType = FormType.CdnDashboard;

  const [reloadLog, setReloadLog] = useState<boolean>(true);
  const { id: application_id } = useParams();

  const { t: tTabs } = useTranslation(Translation.Configuration, { keyPrefix: 'tabs.dashboard' });
  const { t: tTables } = useTranslation(Translation.Description, { keyPrefix: 'configuration.tables' });

  const dashboardLogTableColumns = useMemo(getDashboardTrafficLogsColumns, [tTables]);

  const lastUpdated = useFormLastUpdated(formType);
  const loggedData = useFormList({ formType, endpoint: DashboardEndpoint.TRAFFIC_LOG });
  const isDataLoading = useFormLoadingStatus({ formType, endpoint: DashboardEndpoint.TRAFFIC_LOG });

  const formattedLastUpdated = formatTimestamp(lastUpdated, { format: 'ISO_DATETIME', fallback: '' });

  const handleAutoReload = useCallback(() => {
    setReloadLog((prev) => !prev);
    if (reloadLog) {
      dispatch(cancelAutoReload());
    }
  }, []);

  useEffect(() => {
    if (application_id && reloadLog) {
      dispatch(
        fetchDashboardGraph({
          formType,
          params: { application_id },
          globalOptions: {
            reload: true,
            endpoint: [DashboardEndpoint.TRAFFIC_LOG],
          },
        }),
      );
    }
    return () => {
      dispatch(cancelAutoReload());
    };
  }, [application_id, reloadLog]);

  return (
    <Cards rounded="rounded-2xl px-8 py-7 space-y-6" withoutPadding>
      <div className="flex justify-between">
        <Text $customizeColor $level={3} className="font-medium text-theme-primary-main">
          {tTabs('trafficLogs')}
        </Text>
        <div className="flex gap-5 items-center">
          <Text color="text-gray-500" $level={6}>
            {tTabs('lastUpdated', { date: formattedLastUpdated })}
          </Text>
          <Button
            onClick={handleAutoReload}
            variant={VariantType.Clean}
            className="bg-transparent enabled:shadow-none"
            adornment={
              <ArrowsClockwiseIcon
                className={cn('size-4', reloadLog ? 'animate-spin-gradual text-theme-primary-main' : 'text-zinc-300')}
              />
            }
          />
        </div>
      </div>
      <DraggableTable
        rows={loggedData}
        columns={dashboardLogTableColumns}
        isDataLoading={isDataLoading}
        formType={formType}
      />
    </Cards>
  );
};
